import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { CalendarClock, Save, Ban, CheckCircle2, Clock as ClockIcon, Phone } from "lucide-react";

const STATUS_OPTS = ["new", "confirmed", "contacted", "completed", "rescheduled", "cancelled", "no_show"];
const STATUS_COLOR = {
  new: "bg-blue-500/15 text-blue-300 border-blue-500/40",
  confirmed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  contacted: "bg-cyan-500/15 text-cyan-300 border-cyan-500/40",
  completed: "bg-slate-500/15 text-slate-300 border-slate-500/40",
  rescheduled: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  cancelled: "bg-rose-500/15 text-rose-300 border-rose-500/40",
  no_show: "bg-rose-500/15 text-rose-300 border-rose-500/40",
};
const WEEKDAYS = [["Mon", 1], ["Tue", 2], ["Wed", 3], ["Thu", 4], ["Fri", 5], ["Sat", 6], ["Sun", 7]];

export default function Scheduling() {
  const [tab, setTab] = useState("appointments");
  const [rows, setRows] = useState([]);
  const [avail, setAvail] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get("/scheduling/appointments"),
        api.get("/scheduling/availability"),
      ]);
      setRows(a.data);
      setAvail(b.data);
    } catch (e) { toast.error("Failed to load"); }
  };
  useEffect(() => { load(); }, []);

  const patchRow = async (aid, changes) => {
    try {
      await api.patch(`/scheduling/appointments/${aid}`, changes);
      toast.success("Updated");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const saveAvail = async () => {
    try {
      await api.put("/scheduling/availability", avail);
      toast.success("Availability saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const counts = useMemo(() => {
    const c = {};
    for (const r of rows) c[r.status] = (c[r.status] || 0) + 1;
    return c;
  }, [rows]);

  return (
    <DashboardLayout
      title="Callbacks & Strategy Calls"
      subtitle="Manage appointment requests and control your booking availability"
    >
      <div className="flex items-center gap-2 mb-6" data-testid="sched-tabs">
        {["appointments", "availability"].map((k) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`sched-tab-${k}`}
                  className={`h-9 px-4 rounded-md text-sm font-semibold ${tab === k ? "bg-blue-500 text-white" : "border border-slate-700 text-slate-300"}`}>
            {k === "appointments" ? "Appointments" : "Availability"}
          </button>
        ))}
      </div>

      {tab === "appointments" ? (
        <div data-testid="sched-appts">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-4">
            {STATUS_OPTS.map((s) => (
              <div key={s} className={`text-xs px-3 py-2 rounded-md border ${STATUS_COLOR[s]}`}>
                <div className="font-semibold uppercase tracking-widest">{s.replace("_", " ")}</div>
                <div className="text-lg font-black">{counts[s] || 0}</div>
              </div>
            ))}
          </div>
          <div className="rounded-xl border border-slate-800 bg-slate-900/40 overflow-hidden">
            <div className="grid grid-cols-12 gap-3 px-4 py-3 text-[11px] uppercase tracking-widest text-slate-500 border-b border-slate-800">
              <div className="col-span-3">Client · Business</div>
              <div className="col-span-2">Contact</div>
              <div className="col-span-2">Date · Time · TZ</div>
              <div className="col-span-1">Type</div>
              <div className="col-span-2">Service</div>
              <div className="col-span-2">Status</div>
            </div>
            {rows.length === 0 ? (
              <div className="p-10 text-center text-sm text-slate-500" data-testid="sched-empty">
                <CalendarClock className="w-6 h-6 mx-auto mb-3 opacity-50" /> No appointments yet.
              </div>
            ) : rows.map((r) => (
              <div key={r.appointment_id} data-testid={`sched-row-${r.appointment_id}`}
                   className="border-b border-slate-800/60">
                <div className="grid grid-cols-12 gap-3 px-4 py-3 items-center hover:bg-slate-800/30 cursor-pointer"
                     onClick={() => setExpanded(expanded === r.appointment_id ? null : r.appointment_id)}>
                  <div className="col-span-3">
                    <div className="text-sm font-semibold text-slate-100">{r.contact_name}</div>
                    {r.business_name && <div className="text-xs text-slate-500">{r.business_name}</div>}
                  </div>
                  <div className="col-span-2 text-xs text-slate-400">
                    <div><Phone className="w-3 h-3 inline mr-1" />{r.phone}</div>
                    <div className="truncate">{r.email}</div>
                  </div>
                  <div className="col-span-2 text-xs text-slate-300">
                    {r.date} · <b>{r.time}</b><br />
                    <span className="text-slate-500">{r.timezone}</span>
                  </div>
                  <div className="col-span-1 text-xs uppercase tracking-widest text-slate-400">
                    {r.appointment_type === "strategy_call" ? "Strategy" : "Callback"}
                  </div>
                  <div className="col-span-2 text-xs text-slate-300 truncate">{r.service_interested || "—"}</div>
                  <div className="col-span-2">
                    <span className={`inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-widest border ${STATUS_COLOR[r.status]}`}>
                      {r.status.replace("_", " ")}
                    </span>
                  </div>
                </div>
                {expanded === r.appointment_id && (
                  <div className="px-4 pb-4 pt-2 bg-slate-950/50 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid={`sched-expand-${r.appointment_id}`}>
                    <div className="text-xs text-slate-300 space-y-1">
                      <div><b>Contact method:</b> {(r.contact_method || "phone").replace("_", " ")}</div>
                      {r.city && <div><b>Location:</b> {r.city}{r.state ? `, ${r.state}` : ""}</div>}
                      {r.website && <div><b>Website:</b> {r.website}</div>}
                      {r.industry && <div><b>Industry:</b> {r.industry}</div>}
                      {r.budget && <div><b>Budget:</b> {r.budget}</div>}
                      {r.primary_goal && <div><b>Goal:</b> {r.primary_goal}</div>}
                      {r.notes && <div className="pt-2 border-t border-slate-800/60"><b>Notes:</b><br />{r.notes}</div>}
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="text-[11px] uppercase tracking-widest text-slate-500 block mb-1">Change status</label>
                        <select value={r.status} onChange={(e) => patchRow(r.appointment_id, { status: e.target.value })}
                                data-testid={`sched-status-${r.appointment_id}`}
                                className="w-full h-9 bg-slate-950 border border-slate-800 rounded-md px-2 text-sm">
                          {STATUS_OPTS.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-[11px] uppercase tracking-widest text-slate-500 block mb-1">Internal notes</label>
                        <textarea defaultValue={r.internal_notes || ""}
                                  onBlur={(e) => e.target.value !== (r.internal_notes || "") && patchRow(r.appointment_id, { internal_notes: e.target.value })}
                                  data-testid={`sched-notes-${r.appointment_id}`}
                                  className="w-full bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm" rows={3} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        avail && (
          <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-6 space-y-6 max-w-3xl" data-testid="sched-availability">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Available days</div>
              <div className="flex flex-wrap gap-2">
                {WEEKDAYS.map(([lbl, n]) => {
                  const active = avail.available_weekdays.includes(n);
                  return (
                    <button key={n} data-testid={`wd-${n}`}
                            onClick={() => setAvail({ ...avail, available_weekdays: active ? avail.available_weekdays.filter((x) => x !== n) : [...avail.available_weekdays, n].sort() })}
                            className={`px-3 h-9 rounded-md text-xs font-semibold ${active ? "bg-blue-500 text-white" : "border border-slate-700 text-slate-400"}`}>
                      {lbl}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <label><span className="text-xs uppercase tracking-widest text-slate-500 block mb-1.5">Business hours start</span>
                <input type="time" value={avail.business_hours.start} onChange={(e) => setAvail({ ...avail, business_hours: { ...avail.business_hours, start: e.target.value } })}
                       className="w-full h-9 bg-slate-950 border border-slate-800 rounded-md px-3 text-sm" data-testid="hr-start" /></label>
              <label><span className="text-xs uppercase tracking-widest text-slate-500 block mb-1.5">Business hours end</span>
                <input type="time" value={avail.business_hours.end} onChange={(e) => setAvail({ ...avail, business_hours: { ...avail.business_hours, end: e.target.value } })}
                       className="w-full h-9 bg-slate-950 border border-slate-800 rounded-md px-3 text-sm" data-testid="hr-end" /></label>
              <label><span className="text-xs uppercase tracking-widest text-slate-500 block mb-1.5">Appointment duration (min)</span>
                <input type="number" min="10" step="5" value={avail.appointment_duration_min} onChange={(e) => setAvail({ ...avail, appointment_duration_min: Number(e.target.value) })}
                       className="w-full h-9 bg-slate-950 border border-slate-800 rounded-md px-3 text-sm" data-testid="dur" /></label>
              <label><span className="text-xs uppercase tracking-widest text-slate-500 block mb-1.5">Buffer between (min)</span>
                <input type="number" min="0" step="5" value={avail.buffer_between_min} onChange={(e) => setAvail({ ...avail, buffer_between_min: Number(e.target.value) })}
                       className="w-full h-9 bg-slate-950 border border-slate-800 rounded-md px-3 text-sm" data-testid="buf" /></label>
              <label><span className="text-xs uppercase tracking-widest text-slate-500 block mb-1.5">Min notice (hours)</span>
                <input type="number" min="0" value={avail.min_notice_hours} onChange={(e) => setAvail({ ...avail, min_notice_hours: Number(e.target.value) })}
                       className="w-full h-9 bg-slate-950 border border-slate-800 rounded-md px-3 text-sm" data-testid="notice" /></label>
              <label><span className="text-xs uppercase tracking-widest text-slate-500 block mb-1.5">Max per day</span>
                <input type="number" min="1" value={avail.max_per_day} onChange={(e) => setAvail({ ...avail, max_per_day: Number(e.target.value) })}
                       className="w-full h-9 bg-slate-950 border border-slate-800 rounded-md px-3 text-sm" data-testid="maxday" /></label>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Blocked dates (YYYY-MM-DD, one per line)</div>
              <textarea rows={3} value={(avail.blocked_dates || []).join("\n")}
                        onChange={(e) => setAvail({ ...avail, blocked_dates: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
                        data-testid="blocked"
                        className="w-full bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm" />
            </div>
            <button onClick={saveAvail} data-testid="save-avail"
                    className="btn-scale h-10 px-5 rounded-md bg-blue-500 hover:bg-blue-400 text-white text-sm font-semibold inline-flex items-center gap-2">
              <Save className="w-4 h-4" /> Save availability
            </button>
          </div>
        )
      )}
    </DashboardLayout>
  );
}
